import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProfiles } from "@/hooks/useProfiles";
import { api } from "@/api/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ChevronRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";

const INDUSTRIES = [
  { value: 'ecommerce',      label: 'E-commerce' },
  { value: 'saas',           label: 'SaaS / Technology' },
  { value: 'local_business', label: 'Local Business' },
  { value: 'b2b',            label: 'B2B Services' },
  { value: 'agency',         label: 'Agency' },
  { value: 'other',          label: 'Other' },
];

const PROFILE_COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#EC4899", "#06B6D4", "#F97316",
];

const TIMEZONES = ["UTC", "US/Eastern", "US/Central", "US/Pacific", "Europe/London", "Europe/Berlin", "Asia/Tokyo"];

const initialForm = {
  name: "",
  client_name: "",
  industry: "",
  website: "",
  timezone: "UTC",
  profile_color: "#3B82F6",
  connected_account_id: "",
};

export function CreateProfileModal({ open, onOpenChange }) {
  const { createProfile, isCreating } = useProfiles();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  /** @type {[Record<string, string>, import('react').Dispatch<import('react').SetStateAction<Record<string, string>>>]} */
  const [errors, setErrors] = useState(/** @type {Record<string, string>} */ ({}));
  const [apiError, setApiError] = useState("");
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [createdProfileId, setCreatedProfileId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setCreatedProfileId(null);
    let cancelled = false;
    setAccountsLoading(true);
    api.googleAds
      .accounts()
      .then((res) => {
        if (!cancelled) setConnectedAccounts(res.data.accounts || []);
      })
      .catch(() => {
        if (!cancelled) setConnectedAccounts([]);
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    if (apiError) setApiError("");
  };

  const validateStep1 = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Profile name is required";
    else if (form.name.length > 100) e.name = "Max 100 characters";
    if (!form.client_name.trim()) e.client_name = "Client name is required";
    else if (form.client_name.length > 100) e.client_name = "Max 100 characters";
    if (!form.industry) e.industry = "Industry is required";
    if (form.website && !/^https?:\/\/.+\..+/.test(form.website)) {
      e.website = "Enter a valid URL (https://...)";
    }
    setErrors(/** @type {any} */ (e));
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 2) return;
    if (!validateStep1()) return;

    try {
      setApiError("");
      const payload = {
        name: form.name.trim(),
        client_name: form.client_name.trim(),
        industry: form.industry,
        profile_color: form.profile_color,
        timezone: form.timezone,
      };
      if (form.website?.trim()) payload.website = form.website.trim();

      const newProfile = await createProfile(payload);
      const profileId = newProfile?.id;

      if (form.connected_account_id && profileId) {
        try {
          await api.profiles.linkAccounts(profileId, [form.connected_account_id]);
        } catch (channelErr) {
          console.warn("Profile created but account linking failed:", channelErr);
        }
      }

      setCreatedProfileId(profileId);
      setStep(2);
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to create profile. Please try again.";
      if (err.response?.data?.error === "PROFILE_LIMIT_REACHED") {
        setApiError("Profile limit reached. Upgrade your plan or archive an existing profile.");
      } else if (err.response?.status === 409) {
        setErrors({ name: "A profile with this name already exists" });
        setStep(1);
      } else {
        setApiError(msg);
      }
    }
  };

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setForm(initialForm);
      setErrors({});
      setApiError("");
      setStep(1);
      setCreatedProfileId(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white border-ad-border text-ad-text sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {step === 2 ? "Profile Created!" : "Create New Profile"}
          </DialogTitle>
          {step < 2 && (
            <div className="flex items-center gap-2 pt-2">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center gap-1">
                  <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors",
                    s < step ? "bg-indigo-500 text-white" :
                    s === step ? "bg-indigo-500/30 text-indigo-300 border border-ad-primary" :
                    "bg-white/5 text-ad-muted"
                  )}>
                    {s < step ? <Check className="w-3.5 h-3.5" /> : s}
                  </div>
                  {s < 2 && <div className={cn("w-8 h-0.5", s < step ? "bg-indigo-500" : "bg-white/10")} />}
                </div>
              ))}
              <span className="text-xs text-ad-muted ml-2">Basic Info → Success</span>
            </div>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {apiError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm">
              {apiError}
            </div>
          )}

          {/* ── STEP 1: Basic Info ── */}
          {step === 1 && (
            <>
              <div>
                <Label htmlFor="name">Profile Name *</Label>
                <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g., Acme Marketing Q1 2025"
                  className="mt-1.5 bg-ad-bg border-ad-border text-ad-text" maxLength={100} />
                {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="client_name">Client / Brand Name *</Label>
                <Input id="client_name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)}
                  placeholder="e.g., Acme Corp"
                  className="mt-1.5 bg-ad-bg border-ad-border text-ad-text" maxLength={100} />
                {errors.client_name && <p className="text-xs text-red-400 mt-1">{errors.client_name}</p>}
              </div>

              <div>
                <Label>Industry *</Label>
                <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                  <SelectTrigger className="mt-1.5 bg-ad-bg border-ad-border text-ad-text">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-ad-border text-ad-text">
                    {INDUSTRIES.map((ind) => <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.industry && <p className="text-xs text-red-400 mt-1">{errors.industry}</p>}
              </div>

              <div>
                <Label>Timezone</Label>
                <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
                  <SelectTrigger className="mt-1.5 bg-ad-bg border-ad-border text-ad-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-ad-border text-ad-text">
                    {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input id="website" value={form.website} onChange={(e) => set("website", e.target.value)}
                  placeholder="https://acme.com"
                  className="mt-1.5 bg-ad-bg border-ad-border text-ad-text" />
                {errors.website && <p className="text-xs text-red-400 mt-1">{errors.website}</p>}
              </div>

              <div>
                <Label>Profile Color</Label>
                <div className="flex gap-2 mt-1.5">
                  {PROFILE_COLORS.map((color) => (
                    <button key={color} type="button" onClick={() => set("profile_color", color)}
                      className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
                      style={{ backgroundColor: color, borderColor: form.profile_color === color ? "white" : "transparent" }} />
                  ))}
                </div>
              </div>

              <div>
                <Label>Connected Ad Account</Label>
                <Select value={form.connected_account_id} onValueChange={(v) => set("connected_account_id", v)}>
                  <SelectTrigger className="mt-1.5 bg-ad-bg border-ad-border text-ad-text">
                    <SelectValue placeholder={
                      accountsLoading ? "Loading..." :
                      connectedAccounts.length === 0 ? "No accounts connected" :
                      "Select ad account..."
                    } />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-ad-border text-ad-text">
                    {connectedAccounts.map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        Google Ads — {acc.platform_account_name || acc.platform_account_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.connected_account_id
                  ? <p className="text-xs text-ad-label mt-1">Currency: USD</p>
                  : <p className="text-xs text-ad-muted mt-1">Can be changed later</p>
                }
              </div>
            </>
          )}

          {/* ── STEP 2: Success ── */}
          {step === 2 && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-lg font-medium text-ad-text mb-2">Profile Created Successfully</h3>
              <p className="text-ad-label text-sm mb-6">
                Your profile is ready. You can sync data now or do it later from the profile page.
              </p>
              <div className="flex flex-col gap-3 items-center">
                {form.connected_account_id && createdProfileId && (
                  <Button
                    onClick={async () => {
                      try {
                        await api.profiles.syncAll(createdProfileId);
                      } catch (err) {
                        console.warn("Sync failed:", err);
                      }
                      handleClose(false);
                      navigate(`/profiles/${createdProfileId}`);
                    }}
                    className="glow-button w-full max-w-xs"
                  >
                    Sync Now
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
                <Button
                  onClick={() => {
                    const pid = createdProfileId;
                    handleClose(false);
                    if (pid) navigate(`/profiles/${pid}`);
                  }}
                  variant="outline"
                  className="border-ad-border text-gray-600 hover:bg-white/5 w-full max-w-xs"
                >
                  Go to Profile
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => handleClose(false)}
                  className="text-ad-muted hover:text-gray-600 w-full max-w-xs"
                >
                  Done
                </Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={isCreating} className="glow-button">
                {isCreating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                ) : (
                  <>
                    Create Profile
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
